import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import API from '@/lib/api';
import { WhatsappLogo, MagnifyingGlass, ArrowSquareOut, Copy, Plus, CheckCircle, Receipt, Sparkle, Translate, UserPlus } from '@phosphor-icons/react';
import { toast } from 'sonner';

/**
 * WhatsApp Quick-Send Hub
 *
 * Geen integratie nodig — gebruikt wa.me deep-links.
 * Klanten met geldig GSM-nummer worden getoond. Bij selectie: kies template,
 * vul variabelen ({naam}, {factuurnummer}, {bedrag}, {vervaldatum}, {bedrijfsnaam}),
 * open WhatsApp Web met de tekst klaargemaakt.
 */
export default function WhatsAppHubPage() {
  const [params] = useSearchParams();
  const deepKlantId = params.get('klant_id');
  const deepFactuurId = params.get('factuur_id');
  const deepTemplate = params.get('template');
  const deepPrefillText = params.get('prefilltext');  // raw tekst uit deep-link (bv. vanuit leverbon-verzendmelding)
  const deepQueue = params.get('queue');  // komma-gescheiden lijst klant_ids om sequentieel te verwerken (uit /klantbezoeken)

  // Queue-state: lijst klant_ids die nog verwerkt moeten worden NA huidige klant
  const [queueIds, setQueueIds] = useState(() => (deepQueue ? deepQueue.split(',').filter(Boolean) : []));
  const [queuePrefill, setQueuePrefill] = useState(deepPrefillText || '');

  const [klanten, setKlanten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [klantFacturen, setKlantFacturen] = useState([]);
  const [selectedFactuur, setSelectedFactuur] = useState(null);
  const [bedrijfsnaam, setBedrijfsnaam] = useState('');
  const [log, setLog] = useState([]);
  // ─── Nieuwe state: AI + vrij nummer + taal ────────────────
  const [aiBusy, setAiBusy] = useState(false);
  const [doelTaal, setDoelTaal] = useState('nl');
  const [showFreeNumber, setShowFreeNumber] = useState(false);
  const [freeNumber, setFreeNumber] = useState('');
  const [freeName, setFreeName] = useState('');
  const [filterOnlyOpen, setFilterOnlyOpen] = useState(false);
  const [autoTranslate, setAutoTranslate] = useState(true);

  // ─── Initial load ────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      API.get('/whatsapp/klanten'),
      API.get('/whatsapp/templates'),
      API.get('/instellingen'),
      API.get('/whatsapp/log?limit=10'),
    ]).then(([k, t, m, l]) => {
      setKlanten(k.data?.klanten || []);
      setTemplates(t.data?.items || []);
      setBedrijfsnaam(m.data?.naam || '');
      setLog(l.data?.items || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // ─── Deep-link: auto-select klant + factuur + template via ?klant_id=&factuur_id=&template= ─
  useEffect(() => {
    if (!deepKlantId || klanten.length === 0) return;
    const k = klanten.find(x => x.id === deepKlantId);
    if (k) setSelected(k);
  }, [deepKlantId, klanten]);

  useEffect(() => {
    if (!deepTemplate || templates.length === 0 || !selected) return;
    const tpl = templates.find(t => t.naam.toLowerCase().includes(deepTemplate.toLowerCase()));
    if (tpl) pickTemplate(tpl);
     
  }, [deepTemplate, templates, selected]);

  useEffect(() => {
    if (!deepFactuurId || klantFacturen.length === 0) return;
    const f = klantFacturen.find(x => x.id === deepFactuurId);
    if (f) setSelectedFactuur(f);
  }, [deepFactuurId, klantFacturen]);

  // ─── Deep-link: prefill RAW tekst (bv. vanuit leverbon-verzendmelding) ─────
  // Pas slechts ÉÉN keer toe; daarna mag user vrij van klant wisselen zonder
  // dat de prefill-tekst telkens opnieuw wordt opgedrongen.
  const prefillAppliedRef = useRef(false);
  const prefillKlantIdRef = useRef(null);  // klant waarvoor prefill geldt — wissel weg = wissen
  const originalPrefillRef = useRef(null);
  useEffect(() => {
    if (!deepPrefillText) return;
    if (prefillAppliedRef.current) return;
    if (deepKlantId && !selected) return;  // wacht op klant-selectie
    setMessageText(deepPrefillText);
    setSelectedTemplate(null);  // override eventuele template
    prefillAppliedRef.current = true;
    originalPrefillRef.current = deepPrefillText;
    prefillKlantIdRef.current = selected?.id || deepKlantId || null;
    // Verwijder query-params om herapply na klant-wissel/refresh te voorkomen
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('prefilltext');
      url.searchParams.delete('klant_id');
      url.searchParams.delete('template');
      window.history.replaceState({}, '', url.pathname + (url.search || ''));
    } catch (e) { /* ignore */ }
  }, [deepPrefillText, selected, deepKlantId]);

  // ─── Bij klant-selectie: laad recente facturen + AUTO-SELECT oudste open ───
  useEffect(() => {
    if (!selected) { setKlantFacturen([]); setSelectedFactuur(null); return; }
    setSelectedFactuur(null);
    // Indien er een prefill-tekst voor een andere klant nog staat, wis bij klant-wissel.
    if (originalPrefillRef.current && prefillKlantIdRef.current && selected.id !== prefillKlantIdRef.current) {
      setMessageText('');
      setSelectedTemplate(null);
      originalPrefillRef.current = null;
      prefillKlantIdRef.current = null;
    }
    API.get(`/whatsapp/klant/${selected.id}/recente-facturen`)
      .then(r => {
        const fs = r.data?.facturen || [];
        setKlantFacturen(fs);
        // Auto-select de oudste OPENSTAANDE factuur (= meest dringend) zodat
        // {factuurnummer} en {bedrag} automatisch worden ingevuld.
        // Backend sorteert openstaande ASC op datum → open[0] is de oudste.
        if (!deepFactuurId && fs.length > 0) {
          const open = fs.filter(f => !['betaald', 'geannuleerd', 'credit'].includes((f.status || '').toLowerCase()));
          const oldestOpen = open.length > 0 ? open[0] : null;
          if (oldestOpen) setSelectedFactuur(oldestOpen);
        }
      })
      .catch(() => setKlantFacturen([]));
     
  }, [selected]);

  // ─── Filtering ───────────────────────────────────────────────
  const filteredKlanten = useMemo(() => {
    let arr = klanten;
    if (filterOnlyOpen) arr = arr.filter(k => k.open_facturen > 0);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(k =>
        (k.naam || '').toLowerCase().includes(q) ||
        (k.gsm_raw || '').includes(q) ||
        (k.email || '').toLowerCase().includes(q)
      );
    }
    return arr;
  }, [klanten, search, filterOnlyOpen]);

  // ─── Bij klant-selectie: zet doelTaal automatisch ────────────
  useEffect(() => {
    if (selected?.taal && selected.taal !== doelTaal) {
      setDoelTaal(selected.taal);
    }
     
  }, [selected]);

  // ─── Template-substitutie ────────────────────────────────────
  const substituteVars = useCallback((tekst) => {
    if (!tekst) return tekst;
    const target = showFreeNumber && (freeName || freeNumber)
      ? { naam: freeName || `+${freeNumber.replace(/[^\d]/g, '')}` }
      : selected;
    const f = selectedFactuur || klantFacturen[0] || {};
    const repl = {
      naam: target?.naam || '(klant)',
      bedrijfsnaam: bedrijfsnaam || '(bedrijf)',
      factuurnummer: f.nummer || '(nog te bepalen)',
      bedrag: f.totaal_incl != null ? Number(f.totaal_incl).toLocaleString('nl-BE', { minimumFractionDigits: 2 }) : '(bedrag)',
      vervaldatum: f.vervaldatum ? new Date(f.vervaldatum).toLocaleDateString('nl-BE') : '(vervaldatum)',
      datum: new Date().toLocaleDateString('nl-BE'),
      tijdstip: '09:00-12:00',
    };
    return tekst.replace(/\{(\w+)\}/g, (_, k) => repl[k] != null ? repl[k] : `{${k}}`);
  }, [selected, selectedFactuur, klantFacturen, bedrijfsnaam, showFreeNumber, freeName, freeNumber]);

  const pickTemplate = (tpl) => {
    setSelectedTemplate(tpl);
    const ingevuld = substituteVars(tpl.tekst);
    setMessageText(ingevuld);
    // Auto-vertaal naar klant-taal indien ≠ nl én tekst is niet leeg én autoTranslate enabled
    if (autoTranslate && selected?.taal && selected.taal !== 'nl' && ingevuld) {
      // Niet-blokkerend: vertaling in achtergrond
      setTimeout(async () => {
        setAiBusy(true);
        try {
          const r = await API.post('/whatsapp/ai/vertaal', { tekst: ingevuld, doel_taal: selected.taal });
          if (r.data?.tekst) {
            setMessageText(r.data.tekst);
            toast.success(`Auto-vertaald naar ${r.data.doel_naam} (taal van ${selected.naam})`);
          }
        } catch (e) { /* stil falen */ }
        setAiBusy(false);
      }, 150);
    }
  };

  // ─── Bij wijzigen van geselecteerde factuur: hertype tekst als template gekozen ───
  useEffect(() => {
    if (selectedTemplate && selectedTemplate.tekst) {
      setMessageText(substituteVars(selectedTemplate.tekst));
    }
     
  }, [selectedFactuur]);

  // ─── Open WhatsApp Web met klaargemaakte tekst ───────────────
  const openWhatsApp = async () => {
    if (!messageText) return;
    // Bepaal target nummer/naam: free number of geselecteerde klant
    const target = showFreeNumber && freeNumber
      ? { naam: freeName || `+${freeNumber.replace(/[^\d]/g, '')}`, gsm_normalized: freeNumber.replace(/[^\d]/g, ''), id: null }
      : selected;
    if (!target?.gsm_normalized) {
      toast.error('Geen geldig nummer');
      return;
    }
    const url = `https://wa.me/${target.gsm_normalized}?text=${encodeURIComponent(messageText)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    try {
      await API.post('/whatsapp/log-send', {
        klant_id: target.id,
        klant_naam: target.naam,
        telefoon: target.gsm_normalized,
        template_naam: selectedTemplate?.naam || 'Vrij',
        tekst: messageText,
      });
      const newLog = await API.get('/whatsapp/log?limit=10');
      setLog(newLog.data?.items || []);
      toast.success(`WhatsApp opent voor ${target.naam}`);
    } catch (e) { /* niet kritiek */ }
  };

  // ─── AI Verbeter ────────────────────────────────────────────
  const aiVerbeter = async () => {
    if (!messageText) return;
    setAiBusy(true);
    try {
      const r = await API.post('/whatsapp/ai/verbeter', { tekst: messageText, toon: 'vriendelijk' });
      if (r.data?.tekst) {
        setMessageText(r.data.tekst);
        toast.success('Tekst verbeterd door AI');
      }
    } catch (e) {
      toast.error('AI fout: ' + (e.response?.data?.detail || e.message));
    }
    setAiBusy(false);
  };

  // ─── AI Vertaal ─────────────────────────────────────────────
  const aiVertaal = async (taal) => {
    if (!messageText) return;
    setAiBusy(true);
    try {
      const r = await API.post('/whatsapp/ai/vertaal', { tekst: messageText, doel_taal: taal });
      if (r.data?.tekst) {
        setMessageText(r.data.tekst);
        setDoelTaal(taal);
        toast.success(`Vertaald naar ${r.data.doel_naam}`);
      }
    } catch (e) {
      toast.error('Vertaling mislukt: ' + (e.response?.data?.detail || e.message));
    }
    setAiBusy(false);
  };

  const copyText = () => {
    navigator.clipboard.writeText(messageText);
    toast.success('Tekst gekopieerd');
  };

  // ─── Seed defaults helper ────────────────────────────────────
  const seedTemplates = async () => {
    try {
      const r = await API.post('/whatsapp/templates/seed');
      toast.success(`${r.data?.inserted || 0} sjablonen toegevoegd`);
      const fresh = await API.get('/whatsapp/templates');
      setTemplates(fresh.data?.items || []);
    } catch (e) {
      toast.error('Sjablonen toevoegen mislukt');
    }
  };

  return (
    <div className="p-4 space-y-3" data-testid="whatsapp-hub-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <WhatsappLogo size={32} weight="fill" color="#25D366" />
          WhatsApp Hub
        </h1>
        <div className="flex items-center gap-2">
          {/* Toggle: nieuw nummer (niet uit klantenlijst) */}
          <button
            onClick={() => { setShowFreeNumber(s => !s); if (!showFreeNumber) setSelected(null); }}
            className={`px-3 py-1.5 rounded text-sm font-semibold inline-flex items-center gap-1.5 transition-colors ${showFreeNumber ? 'bg-amber-500 text-white' : 'bg-white border border-amber-400 text-amber-700 hover:bg-amber-50'}`}
            data-testid="wa-toggle-free-number"
          >
            <UserPlus size={14} weight="bold" /> Nieuw nummer
          </button>
          <button
            onClick={() => window.open('https://web.whatsapp.com', '_blank', 'noopener')}
            className="px-3 py-1.5 bg-[#25D366] hover:bg-[#1FAD55] text-white rounded text-sm font-semibold inline-flex items-center gap-1.5"
            data-testid="open-wa-web-btn"
          >
            <ArrowSquareOut size={14} weight="bold" /> Open WhatsApp Web
          </button>
        </div>
      </div>

      {/* Info-banner */}
      <div className="bg-gradient-to-r from-emerald-50 to-green-50 border border-green-200 rounded p-3 text-xs text-green-900 flex items-start gap-2" data-testid="wa-info-banner">
        <CheckCircle size={18} weight="fill" className="text-green-600 flex-shrink-0 mt-0.5" />
        <div>
          <strong>Geen integratie nodig.</strong> Klik op een klant → kies sjabloon → variabelen worden gevuld vanuit BillFac → klik &quot;Open in WhatsApp&quot;.
          De eerste keer scan je 1× de QR op web.whatsapp.com. Daarna werkt het direct.
        </div>
      </div>

      {/* Queue-banner — komt vanuit /klantbezoeken */}
      {queueIds.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-2 border-blue-300 rounded p-3 flex items-center justify-between gap-3" data-testid="wa-queue-banner">
          <div className="text-sm text-blue-900">
            <strong>🧭 Wachtrij vanuit Klantbezoeken</strong> — Nog <strong>{queueIds.length}</strong> {queueIds.length === 1 ? 'klant' : 'klanten'} na deze.
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                // Skip naar volgende
                const [next, ...rest] = queueIds;
                setQueueIds(rest);
                const k = klanten.find(x => x.id === next);
                if (k) {
                  setSelected(k);
                  // Re-trigger prefill
                  if (queuePrefill) {
                    const personal = queuePrefill.replace(/Beste [^,\n]+/, `Beste ${k.naam || ''}`);
                    setMessageText(personal);
                  }
                } else {
                  toast.warning('Klant niet in lijst (mogelijk geblokkeerd of zonder GSM)');
                }
              }}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold"
              data-testid="wa-queue-next"
            >
              Volgende klant →
            </button>
            <button
              type="button"
              onClick={() => { setQueueIds([]); setQueuePrefill(''); toast.info('Wachtrij geannuleerd'); }}
              className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded text-sm font-medium"
              data-testid="wa-queue-cancel"
            >
              Stop wachtrij
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-4">
        {/* ─── Klantenlijst (links) ─────────────────────────── */}
        <div className="bg-white border rounded-lg overflow-hidden flex flex-col" style={{ maxHeight: '70vh' }}>
          <div className="p-3 border-b bg-gray-50 sticky top-0 z-10">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Zoek klant op naam, nummer of email..."
                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                data-testid="wa-search-input"
              />
            </div>
            <div className="flex items-center justify-between mt-1.5">
              <label className="flex items-center gap-1.5 text-[11px] text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterOnlyOpen}
                  onChange={e => setFilterOnlyOpen(e.target.checked)}
                  className="w-3 h-3"
                  data-testid="wa-filter-open"
                />
                <span className="font-bold text-red-700">💰 Alleen klanten met openstaande facturen</span>
              </label>
              <span className="text-[10px] text-gray-500">{filteredKlanten.length}/{klanten.length}</span>
            </div>
          </div>
          <div className="overflow-y-auto flex-1" data-testid="wa-klanten-list">
            {loading && <div className="p-4 text-center text-sm text-gray-500">Laden...</div>}
            {!loading && filteredKlanten.length === 0 && (
              <div className="p-8 text-center text-sm text-gray-500">
                {klanten.length === 0
                  ? <>Nog geen klanten met GSM-nummer.<br/>Voeg een telefoonnummer toe op de klantenfiche.</>
                  : 'Geen klanten gevonden voor zoekterm.'}
              </div>
            )}
            {filteredKlanten.map(k => (
              <button
                key={k.id}
                onClick={() => setSelected(k)}
                className={`w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-green-50 transition-colors ${selected?.id === k.id ? 'bg-green-50 border-l-4 border-l-[#25D366]' : ''}`}
                data-testid={`wa-klant-${k.id}`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-gray-900 truncate flex items-center gap-1">
                      {k.naam}
                      {k.taal && k.taal !== 'nl' && (
                        <span className="text-[10px]" title={`Taal: ${k.taal.toUpperCase()}`}>
                          {k.taal === 'fr' ? '🇫🇷' : k.taal === 'en' ? '🇬🇧' : k.taal === 'de' ? '🇩🇪' : k.taal === 'es' ? '🇪🇸' : k.taal === 'tr' ? '🇹🇷' : `[${k.taal.toUpperCase()}]`}
                        </span>
                      )}
                    </div>
                    {k.contactpersoon && (
                      <div className="text-[10px] text-gray-500 truncate">👤 {k.contactpersoon}</div>
                    )}
                    <div className="text-[11px] text-gray-500 flex items-center gap-1">
                      <span className="font-mono">{k.gsm_raw}</span>
                      {k.prijsgroep && <span className="text-[9px] bg-blue-100 text-blue-700 px-1 rounded">{k.prijsgroep}</span>}
                    </div>
                  </div>
                  {k.open_facturen > 0 && (
                    <div className="text-right flex-shrink-0">
                      <div className="text-[10px] uppercase font-bold text-red-600">{k.open_facturen}× open</div>
                      <div className="text-xs text-red-700 font-bold">€{k.open_bedrag.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}</div>
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* ─── Compose paneel (rechts) ──────────────────────── */}
        <div className="bg-white border rounded-lg p-4 flex flex-col" style={{ maxHeight: '70vh' }}>
          {/* Vrij-nummer modus */}
          {showFreeNumber && (
            <div className="mb-3 pb-3 border-b bg-amber-50 -mx-4 -mt-4 px-4 pt-4" data-testid="wa-free-number-panel">
              <div className="text-xs font-bold text-amber-900 mb-2 flex items-center gap-1.5">
                <UserPlus size={14} weight="bold" /> Vrij nummer (niet uit klantendatabase)
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  value={freeNumber}
                  onChange={e => setFreeNumber(e.target.value)}
                  placeholder="+32 470 12 34 56 of 0470123456"
                  className="px-2 py-1.5 border border-amber-300 rounded text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="wa-free-number-input"
                />
                <input
                  type="text"
                  value={freeName}
                  onChange={e => setFreeName(e.target.value)}
                  placeholder="Naam (optioneel)"
                  className="px-2 py-1.5 border border-amber-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="wa-free-name-input"
                />
              </div>
              <div className="text-[10px] text-amber-700 mt-1">
                Formaat: +32... of 0... (BE) — tabs/spaties worden genegeerd
              </div>
            </div>
          )}

          {!selected && !showFreeNumber ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 text-sm">
              <WhatsappLogo size={64} weight="duotone" className="mb-3 opacity-30" />
              <p>Selecteer een klant of klik &quot;Nieuw nummer&quot; om te beginnen</p>
            </div>
          ) : (
            <>
              {/* Header — klant of vrij nummer */}
              {selected && (
                <div className="mb-3 pb-3 border-b">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs uppercase font-bold text-gray-500">Naar</div>
                      <div className="font-bold text-lg text-gray-900">{selected.naam}</div>
                      {selected.contactpersoon && <div className="text-xs text-gray-600">👤 t.a.v. {selected.contactpersoon}</div>}
                      <div className="text-sm text-gray-600 font-mono">{selected.gsm_raw}</div>
                    </div>
                    {selected.open_facturen > 0 && (
                      <div className="text-right">
                        <div className="text-[10px] uppercase font-bold text-red-600">Openstaand</div>
                        <div className="text-lg font-bold text-red-700">€{selected.open_bedrag.toLocaleString('nl-BE', { minimumFractionDigits: 2 })}</div>
                        <div className="text-[10px] text-gray-500">{selected.open_facturen} factuur(en)</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Factuur-keuze (optioneel) — alleen voor gekoppelde klant */}
              {selected && klantFacturen.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-bold text-gray-700 mb-1.5 flex items-center gap-1">
                    <Receipt size={14} weight="duotone" /> Koppel factuur (vult variabelen)
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {klantFacturen.slice(0, 5).map(f => (
                      <button
                        key={f.id}
                        onClick={() => setSelectedFactuur(selectedFactuur?.id === f.id ? null : f)}
                        className={`text-xs px-2 py-1 rounded border transition-colors ${
                          selectedFactuur?.id === f.id
                            ? 'bg-blue-100 border-blue-400 text-blue-900 font-bold'
                            : f.status === 'betaald' ? 'bg-gray-50 border-gray-200 text-gray-500'
                            : 'bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100'
                        }`}
                        data-testid={`wa-factuur-${f.id}`}
                      >
                        {f.nummer} · €{Number(f.totaal_incl).toLocaleString('nl-BE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        {f.status === 'betaald' && ' ✓'}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Templates */}
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="text-xs font-bold text-gray-700">Kies sjabloon</div>
                  {templates.length === 0 && (
                    <button onClick={seedTemplates} className="text-[10px] text-blue-700 hover:underline">+ Voeg standaard-sjablonen toe</button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {templates.map((tpl, i) => (
                    <button
                      key={tpl.id || i}
                      onClick={() => pickTemplate(tpl)}
                      className={`text-xs px-2.5 py-1 rounded border transition-colors ${selectedTemplate?.naam === tpl.naam ? 'bg-green-100 border-green-500 text-green-900 font-bold' : 'bg-white border-gray-300 hover:bg-green-50'}`}
                      data-testid={`wa-template-${i}`}
                    >
                      {tpl.naam}
                    </button>
                  ))}
                </div>
              </div>

              {/* AI Toolbar: verbeteren + vertalen + auto-translate */}
              <div className="mb-2 flex items-center gap-2 flex-wrap bg-purple-50 border border-purple-200 rounded px-2 py-1.5" data-testid="wa-ai-toolbar">
                <span className="text-[10px] uppercase font-bold text-purple-700">AI</span>
                <button
                  onClick={aiVerbeter}
                  disabled={aiBusy || !messageText}
                  className="text-xs px-2 py-1 bg-white border border-purple-300 text-purple-800 rounded hover:bg-purple-100 inline-flex items-center gap-1 disabled:opacity-50"
                  data-testid="wa-ai-verbeter-btn"
                >
                  <Sparkle size={11} weight="fill" /> {aiBusy ? 'Bezig...' : 'Verbeter tekst'}
                </button>
                <div className="flex items-center gap-1 ml-1">
                  <Translate size={12} className="text-purple-600" />
                  <span className="text-[10px] text-purple-700 font-semibold">Vertaal:</span>
                  {[
                    { c: 'nl', l: '🇳🇱' }, { c: 'fr', l: '🇫🇷' }, { c: 'en', l: '🇬🇧' }, { c: 'de', l: '🇩🇪' }, { c: 'es', l: '🇪🇸' }, { c: 'tr', l: '🇹🇷' },
                  ].map(t => (
                    <button
                      key={t.c}
                      onClick={() => aiVertaal(t.c)}
                      disabled={aiBusy || !messageText}
                      className={`text-sm w-7 h-7 rounded border transition-colors disabled:opacity-40 ${doelTaal === t.c ? 'bg-purple-600 border-purple-600 text-white shadow' : 'bg-white border-purple-200 hover:bg-purple-100'}`}
                      title={`Vertaal naar ${t.c.toUpperCase()}`}
                      data-testid={`wa-ai-vertaal-${t.c}`}
                    >
                      {t.l}
                    </button>
                  ))}
                </div>
                <label className="ml-auto text-[10px] text-purple-800 inline-flex items-center gap-1 cursor-pointer" title="Bij klant met andere taal → template wordt automatisch vertaald">
                  <input
                    type="checkbox"
                    checked={autoTranslate}
                    onChange={e => setAutoTranslate(e.target.checked)}
                    className="w-3 h-3"
                    data-testid="wa-auto-translate"
                  />
                  Auto-vertaal op klant-taal
                </label>
              </div>

              {/* Tekst */}
              <div className="flex-1 flex flex-col mb-3">
                <textarea
                  value={messageText}
                  onChange={e => setMessageText(e.target.value)}
                  placeholder="Typ je bericht hier... Variabelen: {naam} {factuurnummer} {bedrag} {vervaldatum} {bedrijfsnaam}"
                  className="w-full flex-1 p-3 border border-gray-200 rounded text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500"
                  style={{ minHeight: '200px' }}
                  data-testid="wa-message-textarea"
                />
                <div className="flex justify-between text-[10px] text-gray-400 mt-1">
                  <span>{messageText.length} tekens · WhatsApp max 4096</span>
                  <span>{messageText.split('\n').length} regels</span>
                </div>
              </div>

              {/* Actie-knoppen */}
              <div className="flex gap-2">
                <button
                  onClick={openWhatsApp}
                  disabled={!messageText}
                  className="flex-1 px-4 py-2.5 bg-[#25D366] hover:bg-[#1FAD55] disabled:bg-gray-300 text-white rounded font-bold inline-flex items-center justify-center gap-2 transition-colors"
                  data-testid="wa-open-btn"
                >
                  <WhatsappLogo size={16} weight="fill" />
                  Open in WhatsApp
                  <ArrowSquareOut size={14} weight="bold" />
                </button>
                <button
                  onClick={copyText}
                  disabled={!messageText}
                  className="px-3 py-2.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded font-semibold inline-flex items-center gap-2"
                  data-testid="wa-copy-btn"
                  title="Kopieer alleen de tekst"
                >
                  <Copy size={14} weight="bold" />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Activity log */}
      {log.length > 0 && (
        <div className="bg-white border rounded-lg p-3" data-testid="wa-log">
          <div className="text-xs font-bold text-gray-700 mb-2">📋 Laatste verzendingen</div>
          <div className="space-y-1 text-[11px]">
            {log.slice(0, 5).map(l => (
              <div key={l.id} className="flex justify-between text-gray-600 border-b border-gray-50 pb-1">
                <span><strong className="text-gray-900">{l.klant_naam}</strong> ({l.template_naam}) — {l.tekst_preview?.slice(0, 60)}...</span>
                <span className="text-gray-400 whitespace-nowrap ml-2">{new Date(l.sent_at).toLocaleString('nl-BE', { dateStyle: 'short', timeStyle: 'short' })}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
